import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  MailWorkflowService,
  MailTaskDetailResponse,
  MailCommentDto
} from './mail-workflow.service';

type TabId = 'dispatch' | 'forward' | 'bringup' | 'split' | 'outgoing' | 'sign' | 'comments' | 'audit' | 'related' | 'filebrowser';
type LeftTab = 'viewer' | 'filebrowser';

interface WorkflowDoc {
  nodeId: string; name: string; mimeType: string;
  size: number; createdAt: string; isDocx: boolean; isPrimary: boolean;
}
interface UserResult  { id: string; displayName: string; email: string; }
interface GroupResult { id: string; displayName: string; }

@Component({
  selector: 'app-mail-workflow-detail',
  templateUrl: './mail-workflow-detail.component.html',
  styleUrls:  ['./mail-workflow-detail.component.scss']
})
export class MailWorkflowDetailComponent implements OnInit, OnDestroy {

  // ── State ────────────────────────────────────────────────────────────────
  taskId = '';
  task: MailTaskDetailResponse | null = null;
  loading    = true;
  submitting = false;
  error      = '';
  successMsg = '';

  // Original action tab (right panel)
  activeTab: TabId = 'comments';

  // Left panel tab
  leftTab: LeftTab = 'viewer';

  // Accordion open / closed (right panel)
  accMeta     = true;
  accDocs     = true;
  accTrail    = false;
  accActions  = true;
  accComments = false;
  accRelated  = false;
  accAudit    = false;

  // ── Document viewer — blob approach (fixes 401 + X-Frame-Options) ────────
  viewerBlobUrl: SafeResourceUrl | null = null;
  viewerLoading  = false;
  viewerError    = '';
  activeViewerNodeId = '';
  private blobObjectUrl = '';

  // ── Workflow Documents accordion ──────────────────────────────────────────
  workflowDocs:   WorkflowDoc[] = [];
  docsLoading     = false;
  docsLoadError   = '';
  uploadingDoc    = false;
  uploadDocError  = '';
  docsPage        = 1;
  readonly docsPerPage = 10;

  get docsTotalPages(): number { return Math.ceil(this.workflowDocs.length / this.docsPerPage); }
  get pagedDocs(): WorkflowDoc[] {
    const s = (this.docsPage - 1) * this.docsPerPage;
    return this.workflowDocs.slice(s, s + this.docsPerPage);
  }

  // ── DISPATCH form ─────────────────────────────────────────────────────────
  dispatchTo        = '';
  dispatchToName    = '';
  dispatchToGroup   = '';
  dispatchToEmail   = '';
  instructions      = '';
  deadlineAt        = '';
  escalateTo        = '';
  escalateToName    = '';
  notifyHours       = 24;
  escalateHours     = 0;

  // ── FORWARD form + typeahead ──────────────────────────────────────────────
  forwardTo       = '';
  forwardToName   = '';
  forwardToGroup  = '';
  forwardToEmail  = '';
  forwardNote     = '';
  forwardDeadline = '';

  forwardSearchQuery  = '';
  forwardIsGroup      = false;
  forwardUserResults:  UserResult[]  = [];
  forwardGroupResults: GroupResult[] = [];
  forwardSearching     = false;
  showForwardDropdown  = false;
  private forwardSearch$ = new Subject<string>();

  // ── BRINGUP form ──────────────────────────────────────────────────────────
  bringupType = 'DATE';
  bringupDate = '';
  bringupNote = '';

  // ── SPLIT form ────────────────────────────────────────────────────────────
  splitRecipients: Array<{
    username: string; displayName: string; email: string;
    instructions: string; deadlineAt: string;
  }> = [];

  // ── OUTGOING / ARCHIVE ────────────────────────────────────────────────────
  outgoingInstructions = '';
  actionNote = '';

  // ── Comments ──────────────────────────────────────────────────────────────
  newComment        = '';
  replyingToId: number | null = null;
  submittingComment = false;
  currentUserInitials = 'ME';

  // ── File browser ──────────────────────────────────────────────────────────
  fileBrowserFolios: any[] = [];
  fileBrowserLoading = false;
  fileBrowserOrder: 'asc' | 'desc' = 'asc';

  // ── Document signing state ──────────────────────────────────────────────
  /** true while the signing/annotator popup window is open */
  signingInProgress = false;
  /** true once the signing popup was closed (user signed or cancelled) */
  hasSigned = false;

  private destroy$ = new Subject<void>();

  constructor(
    private route:       ActivatedRoute,
    public  router:      Router,
    private mailService: MailWorkflowService,
    private sanitizer:   DomSanitizer
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.taskId = params.get('taskId') ?? '';
      if (this.taskId) this.loadTask();
    });

    // Forward search debounce
    this.forwardSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(q => this._runForwardSearch(q));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._revokeBlobUrl();
  }

  // ── Task loading ──────────────────────────────────────────────────────────

  loadTask(): void {
    this.loading = true;
    this.error   = '';
    this.mailService.getTaskDetail(this.taskId).pipe(takeUntil(this.destroy$)).subscribe({
      next: task => {
        this.task    = task;
        this.loading = false;
        this.activeTab = task.taskType === 'DISPATCH' ? 'dispatch' : 'comments';
        if (task.assigneeName) this.currentUserInitials = this.initials(task.assigneeName);
        if (task.documentNodeId) this.loadDocumentBlob(task.documentNodeId);
        this.loadWorkflowDocs();
      },
      error: () => { this.error = 'Failed to load task. Please try again.'; this.loading = false; }
    });
  }

  // ── Blob viewer ───────────────────────────────────────────────────────────

  loadDocumentBlob(nodeId: string): void {
    if (this.activeViewerNodeId === nodeId) return;
    this._revokeBlobUrl();
    this.viewerLoading = true;
    this.viewerError   = '';
    this.activeViewerNodeId = nodeId;
    this.mailService.getContentBlob(nodeId).pipe(takeUntil(this.destroy$)).subscribe({
      next: blob => {
        this.blobObjectUrl = URL.createObjectURL(blob);
        this.viewerBlobUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobObjectUrl);
        this.viewerLoading = false;
      },
      error: err => {
        this.viewerError   = err?.status === 401 ? 'Unauthorised — please re-login.' : 'Could not load document.';
        this.viewerLoading = false;
      }
    });
  }

  private _revokeBlobUrl(): void {
    if (this.blobObjectUrl) { URL.revokeObjectURL(this.blobObjectUrl); this.blobObjectUrl = ''; }
    this.viewerBlobUrl = null;
  }

  openDocInViewer(nodeId: string, _name?: string): void {
    this.leftTab = 'viewer';
    this.activeViewerNodeId = '';   // force reload
    this.loadDocumentBlob(nodeId);
  }

  get activeViewerName(): string {
    return this.workflowDocs.find(d => d.nodeId === this.activeViewerNodeId)?.name ?? 'Mail Document';
  }

  // ── Workflow Documents ────────────────────────────────────────────────────

  loadWorkflowDocs(): void {
    if (!this.task?.documentNodeId) return;
    this.docsLoading = true; this.docsLoadError = '';
    this.mailService.getWorkflowDocuments(this.task.documentNodeId)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: docs => {
          const pid = this.task!.documentNodeId;
          this.workflowDocs = [
            ...docs.filter(d => d.nodeId === pid).map(d => ({ ...d, isPrimary: true })),
            ...docs.filter(d => d.nodeId !== pid).map(d => ({ ...d, isPrimary: false }))
          ];
          this.docsLoading = false;
        },
        error: () => { this.docsLoadError = 'Could not load documents.'; this.docsLoading = false; }
      });
  }

  onAddDocumentSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file || !this.task) return;
    this.uploadingDoc = true; this.uploadDocError = '';
    this.mailService.addWorkflowDocument(this.task.documentNodeId, file)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => { this.uploadingDoc = false; this.workflowDocs = []; this.loadWorkflowDocs(); },
        error: err => { this.uploadingDoc = false; this.uploadDocError = err?.error?.message ?? 'Upload failed.'; }
      });
  }

  docsNextPage(): void { if (this.docsPage < this.docsTotalPages) this.docsPage++; }
  docsPrevPage(): void { if (this.docsPage > 1) this.docsPage--; }

  // ── Forward typeahead ─────────────────────────────────────────────────────

  onForwardSearchInput(q: string): void {
    this.forwardSearchQuery = q;
    this.forwardTo = q;
    this.showForwardDropdown = q.trim().length >= 2;
    if (q.trim().length >= 2) this.forwardSearch$.next(q);
    else { this.forwardUserResults = []; this.forwardGroupResults = []; }
  }

  private _runForwardSearch(q: string): void {
    if (!q.trim()) return;
    this.forwardSearching = true;
    if (!this.forwardIsGroup) {
      this.mailService.searchUsers(q).pipe(takeUntil(this.destroy$)).subscribe({
        next: r => { this.forwardUserResults = r; this.forwardSearching = false; },
        error: () => this.forwardSearching = false
      });
    } else {
      this.mailService.searchGroups(q).pipe(takeUntil(this.destroy$)).subscribe({
        next: r => { this.forwardGroupResults = r; this.forwardSearching = false; },
        error: () => this.forwardSearching = false
      });
    }
  }

  selectForwardUser(u: UserResult): void {
    this.forwardTo = u.id; this.forwardToName = u.displayName;
    this.forwardToEmail = u.email; this.forwardToGroup = '';
    this.forwardSearchQuery = u.displayName; this.showForwardDropdown = false;
    this.forwardUserResults = [];
  }

  selectForwardGroup(g: GroupResult): void {
    this.forwardToGroup = g.id; this.forwardToName = g.displayName;
    this.forwardTo = ''; this.forwardSearchQuery = g.displayName;
    this.showForwardDropdown = false; this.forwardGroupResults = [];
  }

  clearForwardSelection(): void {
    this.forwardTo = ''; this.forwardToName = ''; this.forwardToGroup = '';
    this.forwardToEmail = ''; this.forwardSearchQuery = '';
  }

  // ── Task type helpers ─────────────────────────────────────────────────────

  get isDispatchTask(): boolean { return this.task?.taskType === 'DISPATCH'; }
  get isInboxTask():    boolean { return this.task?.taskType === 'INBOX';    }
  get isMyTask():       boolean { return this.task?.myTask ?? false;         }

  setTab(tab: TabId): void {
    this.activeTab = tab;
    if (tab === 'filebrowser' && this.fileBrowserFolios.length === 0) this.loadFileBrowser();
  }

  get priorityClass(): string {
    return { URGENT: 'urgent', CONFIDENTIAL: 'confidential', NORMAL: 'normal' }
      [this.task?.priority ?? 'NORMAL'] ?? 'normal';
  }

  get mailTypeLabel(): string {
    return { INCOMING: 'Incoming', OUTGOING: 'Outgoing', INTERNAL_MEMO: 'Internal Memo' }
      [this.task?.mailType ?? ''] ?? (this.task?.mailType ?? '');
  }

  get mailTypeIcon(): string {
    return { INCOMING: 'move_to_inbox', OUTGOING: 'outgoing_mail', INTERNAL_MEMO: 'sticky_note_2' }
      [this.task?.mailType ?? ''] ?? 'mail';
  }

  get mailTypeColor(): string {
    return { INCOMING: '#244062', OUTGOING: '#0D7A5F', INTERNAL_MEMO: '#D46B08' }
      [this.task?.mailType ?? ''] ?? '#6c757d';
  }

  get priorityColor(): string {
    return { URGENT: '#dc3545', CONFIDENTIAL: '#856404', NORMAL: '#6c757d' }
      [this.task?.priority ?? 'NORMAL'] ?? '#6c757d';
  }

  get stageColor(): string {
    return { DISPATCH: '#D46B08', INBOX: '#2B5CB8', BRINGUP: '#6B21A8', ARCHIVED: '#6c757d' }
      [this.task?.currentStage ?? ''] ?? '#6c757d';
  }

  // ── Complete task (original methods preserved) ────────────────────────────

  completeDispatch(): void {
    if (!this.dispatchTo.trim()) { this.error = 'Recipient is required.'; return; }
    this.submit('DISPATCH', {
      dispatchTo: this.dispatchTo, dispatchToName: this.dispatchToName,
      dispatchToGroup: this.dispatchToGroup, dispatchToEmail: this.dispatchToEmail,
      instructions: this.instructions, deadlineAt: this.deadlineAt || null,
      escalateTo: this.escalateTo || null, escalateToName: this.escalateToName || null,
      notifyAtHoursBefore: this.notifyHours, escalateAtHoursBefore: this.escalateHours
    });
  }

  completeForward(): void {
    if (!this.forwardTo.trim() && !this.forwardToGroup.trim()) {
      this.error = 'Select a user or group to forward to.'; return;
    }
    this.submit('FORWARD', {
      forwardTo: this.forwardTo, forwardToName: this.forwardToName,
      forwardToGroup: this.forwardToGroup, forwardToEmail: this.forwardToEmail,
      forwardNote: this.forwardNote, deadlineAt: this.forwardDeadline || null
    });
  }

  completeBringup(): void {
    if (this.bringupType === 'DATE' && !this.bringupDate) { this.error = 'Bring-up date is required.'; return; }
    if (this.bringupType === 'CONDITION' && !this.bringupNote.trim()) { this.error = 'Condition is required.'; return; }
    this.submit('BRINGUP', { bringupType: this.bringupType, bringupDate: this.bringupDate || null, bringupNote: this.bringupNote || null });
  }

  completeSplit(): void {
    if (!this.splitValid) { this.error = 'Add at least one recipient with username and email.'; return; }
    this.submit('SPLIT', { splitRecipients: this.splitRecipients });
  }

  completeCreateOutgoing(): void { this.submit('CREATE_OUTGOING', { outgoingInstructions: this.outgoingInstructions }); }

  completeArchive(): void { this.submit('ARCHIVE', { note: this.actionNote }); }

  addSplitRecipient(): void {
    this.splitRecipients.push({ username: '', displayName: '', email: '', instructions: '', deadlineAt: '' });
  }
  removeSplitRecipient(i: number): void { this.splitRecipients.splice(i, 1); }

  get splitValid(): boolean {
    return this.splitRecipients.length > 0 &&
           this.splitRecipients.every(r => r.username.trim() && r.email.trim());
  }

  startOutgoingReply(): void {
    if (!this.task) return;
    this.router.navigate(['/mail-workflow/new'], {
      queryParams: {
        mailType: 'OUTGOING', inReplyTo: this.task.documentNodeId,
        inReplyToRef: this.task.referenceNumber,
        subject: 'RE: ' + this.task.subject,
        toOrg: this.task.fromOrganization || this.task.fromName || ''
      }
    });
  }

  releaseBringup(): void {
    if (!this.task) return;
    this.submitting = true;
    this.mailService.releaseBringup(this.task.documentNodeId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.successMsg = 'Bring-up released. Document returned to INBOX.'; this.submitting = false; this.loadTask(); },
      error: () => { this.error = 'Failed to release bring-up.'; this.submitting = false; }
    });
  }

  private submit(decision: string, extra: any = {}): void {
    this.submitting = true; this.error = ''; this.successMsg = '';
    this.mailService.completeTask(this.taskId, { decision, note: this.actionNote, ...extra })
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.successMsg = `Action "${decision}" completed successfully.`;
          this.submitting = false;
          setTimeout(() => this.router.navigate(['/workflow']), 1200);
        },
        error: err => { this.error = err?.error?.message ?? 'Failed to complete action.'; this.submitting = false; }
      });
  }

  // ── Comments (original) ───────────────────────────────────────────────────

  submitComment(): void {
    if (!this.newComment.trim() || !this.task) return;
    this.submittingComment = true;
    this.mailService.addComment(this.taskId, this.task.documentNodeId, this.newComment, this.replyingToId ?? undefined)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => { this.newComment = ''; this.replyingToId = null; this.submittingComment = false; this.loadTask(); },
        error: () => this.submittingComment = false
      });
  }

  replyTo(comment: MailCommentDto): void { this.replyingToId = comment.id; this.activeTab = 'comments'; this.accComments = true; }
  cancelReply(): void { this.replyingToId = null; }

  // ── File browser (original) ───────────────────────────────────────────────

  loadFileBrowser(): void {
    if (!this.task?.fileReferenceId) {
      // No file reference set — show empty state
      this.fileBrowserFolios = [];
      return;
    }
    this.fileBrowserLoading = true;
    this.mailService.getFileFolios(this.task.fileReferenceId, this.fileBrowserOrder)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: f => { this.fileBrowserFolios = f; this.fileBrowserLoading = false; },
        error: () => { this.fileBrowserFolios = []; this.fileBrowserLoading = false; }
      });
  }

  toggleFolioOrder(): void {
    this.fileBrowserOrder = this.fileBrowserOrder === 'asc' ? 'desc' : 'asc';
    this.fileBrowserFolios = [];
    this.loadFileBrowser();
  }

  // ── Display helpers ───────────────────────────────────────────────────────

  initials(name: string): string {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  auditIcon(action: string): string {
    const m: Record<string, string> = {
      WORKFLOW_STARTED: 'play_circle', DISPATCHED: 'send', FORWARDED: 'forward_to_inbox',
      BRINGUP: 'schedule', BRINGUP_RELEASED: 'alarm_on', SPLIT: 'call_split',
      CREATE_OUTGOING: 'reply', ARCHIVED: 'inventory_2', ESCALATED: 'warning',
      WORKFLOW_COMPLETED: 'check_circle'
    };
    return m[action] ?? 'radio_button_unchecked';
  }

  docIcon(doc: WorkflowDoc): string {
    if (doc.mimeType?.includes('pdf'))  return 'picture_as_pdf';
    if (doc.mimeType?.includes('word') || doc.isDocx) return 'description';
    if (doc.mimeType?.includes('image')) return 'image';
    return 'insert_drive_file';
  }

  docIconColor(doc: WorkflowDoc): string {
    if (doc.mimeType?.includes('pdf'))  return '#dc3545';
    if (doc.mimeType?.includes('word') || doc.isDocx) return '#2B5CB8';
    if (doc.mimeType?.includes('image')) return '#28a745';
    return '#6c757d';
  }

  formatSize(bytes: number): string {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  // ── Document Signing ─────────────────────────────────────────────────────

  /**
   * Opens the SkyPlus Annotator popup for signing the task's primary document.
   * After the popup closes, reloads the document blob so the signed version
   * (uploaded by the annotator to Alfresco) is shown immediately in the
   * left-panel viewer.
   *
   * Mirrors SkyPlusDocumentLibraryComponent.signDocument() — the URL is
   * built on the frontend from the Alfresco nodeId and the authenticated
   * ECM username; signing is gated on the task being assigned to the user.
   */
  signDocument(): void {
    if (!this.task?.documentNodeId) {
      this.error = 'No document available to sign.';
      return;
    }
    if (!this.task.myTask) {
      this.error = 'Signing is only available when this task is assigned to you.';
      return;
    }
    this.signingInProgress = true;
    this.mailService.signDocument(this.task.documentNodeId, () => {
      // Annotator popup closed — user either signed or cancelled
      this.signingInProgress = false;
      this.hasSigned = true;
      // Reload blob — Alfresco returns the latest (now signed) version automatically
      this.reloadDocumentAfterSigning();
    });
  }

  
  /**
   * After the annotator uploads the signed PDF to Alfresco, reload the
   * left-panel viewer. Same nodeId — Alfresco serves the latest version
   * transparently.
   */
  reloadDocumentAfterSigning(): void {
    if (!this.task?.documentNodeId) return;
    // Force a fresh blob load by clearing the active viewer state
    this.activeViewerNodeId = '';
    this.loadDocumentBlob(this.task.documentNodeId);
  }
}